import Layout from "../../components/layout";
import Seo from "../../components/seo";
import Header from "../../components/header";
import Footer from "../../components/footer";
import CTAButton from "../../components/CTAButton";
import ServiceCTA from "../../components/ServiceCTA";
import ServiceHero from "../../components/ServiceHero";
import ContentImageSection from "../../components/sections/ContentImageSection";
import { InfoCard, TestimonialHighlight } from "../../components/sections";
import LogoSlider from "../../components/sections/LogoSlider";
import { FiClock, FiShield, FiCpu, FiActivity } from "react-icons/fi";
import bgImage from "../../images/cases/onscript-ui.png";
import onscriptLogo from "../../images/cases/onscript-logo.png";
import { useNavigate } from "react-router-dom";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const OnScriptProjectPage = () => {
  const navigate = useNavigate();

  usePageMetadata({
    title: "OnScript.ai Case Study - Bercastle",
    description:
      "How Bercastle built AI-powered speech analytics for OnScript.ai to evaluate every call with precision and consistency.",
    pageType: "case_study",
    content: {
      project: {
        name: "OnScript.ai",
        industry: "Contact Center / Speech Analytics",
        summary:
          "Boost agent performance and maximize ROI with AI QA that scores every call and surfaces coachable moments automatically.",
        url: "/portfolio/onscript-ai",
        external_url: "https://onscript.ai",
        metrics: [
          { label: "QA coverage", value: "100% of calls" },
          { label: "Feedback speed", value: "Minutes, not days" },
          { label: "Uptime", value: "99.9% SLO" },
        ],
      },
      meta: {
        source_url: "https://bercastle.com/portfolio/onscript-ai",
      },
    },
  });

  const snapshotCards = [
    {
      icon: FiClock,
      title: "Live in 8 weeks",
      description: "Shipped production speech-analytics MVP with call ingestion, scoring, and dashboards.",
    },
    {
      icon: FiShield,
      title: "Quality guardrails",
      description: "Policy checks, PII redaction, sentiment + silence detection, and calibration controls.",
    },
    {
      icon: FiCpu,
      title: "Signal orchestration",
      description: "Combines ASR, LLM scoring, and rules engines with caching and fallbacks for speed.",
    },
    {
      icon: FiActivity,
      title: "Observability",
      description: "Tracing from raw audio to QA score so supervisors can trust every evaluation.",
    },
  ];

  const outcomes = [
    { label: "QA coverage", value: "100% of calls scored" },
    { label: "Time to feedback", value: "Near real-time" },
    { label: "Coaching productivity", value: "Higher focus on outliers" },
    { label: "Uptime", value: "99.9% SLO" },
  ];

  const stackIcons = [
    { src: 'https://skillicons.dev/icons?i=python', alt: "Python", label: "Python" },
    { src: 'https://skillicons.dev/icons?i=fastapi', alt: "FastAPI", label: "FastAPI" },
    { src: 'https://skillicons.dev/icons?i=postgres', alt: "PostgreSQL", label: "PostgreSQL" },
    { src: 'https://skillicons.dev/icons?i=redis', alt: "Redis", label: "Redis" },
    { src: 'https://skillicons.dev/icons?i=react', alt: "React", label: "React" },
    { src: 'https://skillicons.dev/icons?i=typescript', alt: "TypeScript", label: "TypeScript" },
    { src: 'https://skillicons.dev/icons?i=docker', alt: "Docker", label: "Docker" },
    { src: 'https://skillicons.dev/icons?i=kubernetes', alt: "Kubernetes", label: "Kubernetes" },
    { src: 'https://skillicons.dev/icons?i=nginx', alt: "Nginx", label: "Nginx" },
    { src: 'https://skillicons.dev/icons?i=aws', alt: "AWS", label: "AWS" },
    { src: 'https://skillicons.dev/icons?i=github', alt: "GitHub", label: "GitHub" },
    { src: 'https://skillicons.dev/icons?i=vite', alt: "Vite", label: "Vite" },
  ];

  return (
    <Layout classmain="portfolio-onscript">
      <Seo
        title="OnScript.ai Case Study - Bercastle"
        metaDesciption="How Bercastle built the AI infrastructure behind OnScript.ai: generative workflows, approvals, and production reliability."
      />
      <Header />

      {/* Hero */}
      <ServiceHero
        badge="Case Study · Speech Analytics"
        title="OnScript.ai"
        logo={onscriptLogo}
        description="Boost agent performance and maximize ROI with AI-powered speech analytics that evaluates every call for precision, compliance, and coaching."
        externalUrl="https://onscript.ai"
        externalUrlText="Visit OnScript.ai"
        ctaText="Start a similar build"
        ctaLink="/contact"
      />

      {/* Flow + image */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <ContentImageSection image={bgImage} imageAlt="OnScript AI workflow">
            <div className="space-y-6">
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900">
                QA that listens to every call
              </h3>
              <p className="text-lg text-slate-700 leading-relaxed">
                OnScript.ai is an AI-powered speech analytics platform designed for contact centers. It automatically evaluates every customer interaction by analyzing call recordings, transcribing conversations, and applying intelligent scoring across compliance, empathy, tone, silence, interruptions, and more.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Instead of traditional QA where supervisors manually score a small sample (5-10%), OnScript.ai enables 100% coverage. Every agent interaction gets evaluated consistently, every coachable moment surfaces automatically, and leadership can focus on outliers instead of manual spreadsheets.
              </p>
              <ul className="space-y-3 text-slate-700 text-lg leading-relaxed list-disc list-inside">
                <li>Real-time ingestion from call recordings with accurate transcription.</li>
                <li>LLM + rules scoring for compliance, empathy, tone, silence, and interruptions.</li>
                <li>Supervisor console for calibrations, overrides, and coaching notes.</li>
                <li>Dashboards highlighting outliers and ROI so leaders act faster.</li>
              </ul>
            </div>
          </ContentImageSection>
        </div>
      </section>

      {/* Snapshot */}
      <section className="py-16 md:py-24">
        <div className="container-custom space-y-10">
          <div className="max-w-3xl">
            <p className="text-[#456EF2] font-montserrat font-semibold text-sm uppercase tracking-wide mb-3">
              Snapshot
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What we built</h2>
            <p className="text-lg text-slate-600">
              A production-grade pipeline that keeps editors in control while AI handles the heavy lifting—intake, enrichment, generation, and approvals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {snapshotCards.map((card) => (
              <InfoCard
                key={card.title}
                icon={card.icon}
                title={card.title}
                iconSize="lg"
              >
                {card.description}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>



      {/* Outcomes */}
      <section className="py-16 md:py-24">
        <div className="container-custom space-y-10">
          <div className="max-w-3xl">
            <p className="text-[#456EF2] font-montserrat font-semibold text-sm uppercase tracking-wide mb-3">
              Outcomes
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Measured impact</h2>
            <p className="text-lg text-slate-600">
              Metrics that matter to QA leaders: full coverage, fast feedback loops, and trusted evaluations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {outcomes.map((item) => (
              <InfoCard
                key={item.label}
                title={item.label}
              >
                {item.value}
              </InfoCard>
            ))}
          </div>
        </div>
      </section>

      {/* Stack & governance */}
      <section className="py-16 md:py-24">
        <div className="container-custom ">
          <div className="space-y-6">
            <h3 className="text-4xl md:text-5xl font-bold text-center mb-12">Architecture & stack</h3>
            <LogoSlider items={stackIcons} variant="image" />
          </div>
        </div>
      </section>

      <ServiceCTA
        title="Need AI-powered QA for your contact center?"
        description="Let’s score every call, surface coaching moments, and prove ROI with reliable speech analytics."
        buttonText="Talk to the team"
      />

      <Footer />
    </Layout>
  );
};

export default OnScriptProjectPage;
